/**
 * Shared scaffolding for the maintained simulations.
 *
 * SVG rather than canvas throughout: it stays crisp at any size, it is inspectable in devtools
 * (which matters, because "verify visuals before claiming they work" is a rule we have to be able
 * to follow), and it gives every moving part a real element that can carry a label.
 *
 * The helpers here exist to make the rules in Tutor-reference/docs/principles.md cheap to obey:
 *
 *   readouts()  numbers go through fmt(), which never renders a non-zero value as "0"
 *   meter()     a meter is declared as a fixed set of buckets plus a total, and the total row
 *               shows the sum, so a missing bucket is visible instead of silent
 *   loop()      one rAF loop per sim, stopped on destroy, with a clamped dt so a backgrounded
 *               tab cannot teleport the physics
 */

import { append, el, fmt, fmtPercent } from "../dom.js";
import { track } from "../telemetry.js";

export const SVG_NS = "http://www.w3.org/2000/svg";

export type Attrs = Record<string, string | number | undefined>;

export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: SVGElement[] = [],
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  setAttrs(node, attrs);
  for (const child of children) node.appendChild(child);
  return node;
}

export function setAttrs(node: Element, attrs: Attrs): void {
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined) continue;
    node.setAttribute(key, typeof value === "number" ? round(value) : value);
  }
}

/** Four decimals is plenty for screen coordinates and keeps the DOM readable. */
function round(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(4).replace(/\.?0+$/, "");
}

export function svgText(x: number, y: number, content: string, attrs: Attrs = {}): SVGTextElement {
  const node = svgEl("text", {
    x,
    y,
    fill: "currentColor",
    "font-size": 14,
    "font-family":
      "-apple-system, BlinkMacSystemFont, Inter, 'Segoe UI', system-ui, sans-serif",
    ...attrs,
  });
  node.textContent = content;
  return node;
}

// ────────────────────────────────── parameters ──────────────────────────────────

/**
 * `unknown` values, not scalars: the spec schema deliberately does not police param types (see the
 * note on `params` in `src/lesson/spec.ts`), so a sim can be handed anything a model wrote. Every
 * reader below already type-checks and falls back, which is what makes that safe.
 */
export type SimParams = Record<string, unknown>;

export function numParam(
  params: SimParams,
  key: string,
  fallback: number,
  min = -Infinity,
  max = Infinity,
): number {
  const raw = params[key];
  const value = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

export function strParam(params: SimParams, key: string, fallback: string): string {
  const raw = params[key];
  return typeof raw === "string" && raw.trim() ? raw.trim() : fallback;
}

export function boolParam(params: SimParams, key: string, fallback: boolean): boolean {
  const raw = params[key];
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw === "true" || raw === "1";
  if (typeof raw === "number") return raw !== 0;
  return fallback;
}

/** Splits a delimited list param ("egg, caterpillar, pupa") into trimmed, capped entries. */
export function listParam(params: SimParams, key: string, fallback: string[]): string[] {
  const raw = params[key];
  if (typeof raw !== "string") return fallback;
  const parts = raw
    .split(/\s*[|,]\s*/)
    .map((part) => part.trim().slice(0, 48))
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts : fallback;
}

// ────────────────────────────────── scaffolding ──────────────────────────────────

export interface Scaffold {
  root: HTMLElement;
  stage: SVGSVGElement;
  readouts: HTMLElement;
  /** Between the readouts and the controls: meters, legends, extra text. */
  extras: HTMLElement;
  controls: HTMLElement;
  caption: HTMLElement;
}

export function scaffold(
  host: HTMLElement,
  options: { width: number; height: number; hint?: string; label: string },
): Scaffold {
  const root = el("div", { class: "sim" });
  if (options.hint) root.appendChild(el("p", { class: "sim-hint", text: options.hint }));

  const stage = svgEl("svg", {
    class: "sim-stage",
    viewBox: `0 0 ${options.width} ${options.height}`,
    role: "img",
    "aria-label": options.label,
    color: "var(--sim-ink)",
  });
  // The stage is decorative *around* the interactive parts; each interactive part gets its own
  // accessible control, and the numbers are duplicated as text in the readouts below.
  root.appendChild(stage);

  const readouts = el("div", { class: "sim-readouts", attrs: { role: "status", "aria-live": "polite" } });
  const extras = el("div", { class: "sim-extras" });
  const controls = el("div", { class: "sim-controls" });
  const caption = el("p", { class: "sim-caption" });
  append(root, [readouts, extras, controls, caption]);
  host.appendChild(root);
  return { root, stage, readouts, extras, controls, caption };
}

// ────────────────────────────────── readouts ──────────────────────────────────

export interface Readouts {
  set(key: string, value: string): void;
  setNumber(key: string, value: number, digits?: number, unit?: string): void;
}

export function readouts(container: HTMLElement, keys: readonly string[]): Readouts {
  const values = new Map<string, HTMLElement>();
  for (const key of keys) {
    const value = el("span", { class: "v", text: "—" });
    container.appendChild(
      el("span", { class: "sim-readout" }, [el("span", { class: "k", text: key }), value]),
    );
    values.set(key, value);
  }
  return {
    set(key, value) {
      const node = values.get(key);
      if (node) node.textContent = value;
    },
    setNumber(key, value, digits = 1, unit = "") {
      const node = values.get(key);
      // fmt() is the guard against a readout saying "0" while the thing is still moving.
      if (node) node.textContent = `${fmt(value, digits)}${unit ? ` ${unit}` : ""}`;
    },
  };
}

// ──────────────────────────────────── meters ────────────────────────────────────

export interface MeterBucket {
  key: string;
  label: string;
  colour: string;
}

export interface Meter {
  /** Values in the same unit as `total`. Every declared bucket must be supplied. */
  update(values: Record<string, number>, total: number): void;
}

/**
 * A conservation meter.
 *
 * principles.md: "a meter must account for ALL the energy" — if the chart leaves out the bucket
 * the energy actually went into, the total visibly fails to add up and the lesson is undermined.
 * So the total row is computed from the sum of the buckets and compared against the declared
 * total; if a bucket were missing, the mismatch would show on screen rather than hide.
 */
export function meter(
  container: HTMLElement,
  title: string,
  buckets: readonly MeterBucket[],
): Meter {
  const wrap = el("div", { class: "meter" });
  wrap.appendChild(el("div", { class: "meter-title", text: title }));
  const fills = new Map<string, HTMLElement>();
  const labels = new Map<string, HTMLElement>();

  for (const bucket of buckets) {
    const fill = el("div", { class: "meter-fill", style: { background: bucket.colour } });
    const value = el("span", { class: "meter-value", text: "0%" });
    wrap.appendChild(
      el("div", { class: "meter-row" }, [
        el("span", { class: "meter-label", text: bucket.label }),
        el("div", { class: "meter-track" }, [fill]),
        value,
      ]),
    );
    fills.set(bucket.key, fill);
    labels.set(bucket.key, value);
  }

  const totalFill = el("div", { class: "meter-fill", style: { background: "var(--sim-neutral)" } });
  const totalValue = el("span", { class: "meter-value", text: "0%" });
  wrap.appendChild(
    el("div", { class: "meter-row meter-total" }, [
      el("span", { class: "meter-label", text: "All of it" }),
      el("div", { class: "meter-track" }, [totalFill]),
      totalValue,
    ]),
  );
  container.appendChild(wrap);

  return {
    update(values, total) {
      let sum = 0;
      for (const bucket of buckets) {
        const amount = values[bucket.key] ?? 0;
        sum += amount;
        const fraction = total > 0 ? amount / total : 0;
        fills.get(bucket.key)?.style.setProperty("width", `${(fraction * 100).toFixed(2)}%`);
        const label = labels.get(bucket.key);
        if (label) label.textContent = fmtPercent(fraction);
      }
      const totalFraction = total > 0 ? sum / total : 0;
      totalFill.style.setProperty("width", `${Math.min(100, totalFraction * 100).toFixed(2)}%`);
      totalValue.textContent = fmtPercent(totalFraction);
    },
  };
}

// ─────────────────────────────────── animation ───────────────────────────────────

export interface Loop {
  stop(): void;
}

/**
 * One requestAnimationFrame loop per sim.
 *
 * `dt` is clamped to 50ms. Without the clamp, returning to a backgrounded tab delivers one
 * enormous frame and the box teleports through the wall — which looks like broken physics but is
 * really a broken integrator.
 */
export function loop(step: (dt: number) => void): Loop {
  let handle = 0;
  let last = performance.now();
  let running = true;
  const tick = (now: number): void => {
    if (!running) return;
    const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
    last = now;
    step(dt);
    handle = requestAnimationFrame(tick);
  };
  handle = requestAnimationFrame(tick);
  return {
    stop() {
      running = false;
      cancelAnimationFrame(handle);
    },
  };
}

// ───────────────────────────────── pointer geometry ─────────────────────────────────

/** Pointer position in the stage's own coordinate system, accounting for CSS scaling. */
export function stagePoint(stage: SVGSVGElement, event: PointerEvent): { x: number; y: number } {
  const rect = stage.getBoundingClientRect();
  const box = stage.viewBox.baseVal;
  const width = box && box.width > 0 ? box.width : rect.width;
  const height = box && box.height > 0 ? box.height : rect.height;
  if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
  return {
    x: ((event.clientX - rect.left) / rect.width) * width,
    y: ((event.clientY - rect.top) / rect.height) * height,
  };
}

/**
 * Makes an element genuinely draggable.
 *
 * "Make the obvious affordance work": anything that looks grabbable gets wired through here, and
 * it also gets a keyboard path, because a drag-only control is unusable for some children.
 */
export function draggable(
  stage: SVGSVGElement,
  handle: SVGElement,
  callbacks: {
    onStart?(point: { x: number; y: number }): void;
    onMove(point: { x: number; y: number }): void;
    onEnd?(point: { x: number; y: number }): void;
  },
): () => void {
  let active = false;
  const down = (event: PointerEvent): void => {
    active = true;
    handle.setPointerCapture(event.pointerId);
    event.preventDefault();
    callbacks.onStart?.(stagePoint(stage, event));
  };
  const move = (event: PointerEvent): void => {
    if (!active) return;
    event.preventDefault();
    callbacks.onMove(stagePoint(stage, event));
  };
  const up = (event: PointerEvent): void => {
    if (!active) return;
    active = false;
    if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
    callbacks.onEnd?.(stagePoint(stage, event));
  };
  handle.addEventListener("pointerdown", down);
  handle.addEventListener("pointermove", move);
  handle.addEventListener("pointerup", up);
  handle.addEventListener("pointercancel", up);
  handle.style.setProperty("cursor", "grab");
  handle.style.setProperty("touch-action", "none");
  return () => {
    handle.removeEventListener("pointerdown", down);
    handle.removeEventListener("pointermove", move);
    handle.removeEventListener("pointerup", up);
    handle.removeEventListener("pointercancel", up);
  };
}

// ─────────────────────────────────── telemetry ───────────────────────────────────

/**
 * Emits an `interact` event attributed to whichever block the sim is mounted inside.
 *
 * Sims call this rather than receiving an emitter, so `createSim(kind, params, host)` keeps the
 * signature the renderer expects while still being instrumented automatically.
 */
export function simTrack(host: HTMLElement, action: string, value?: string | number | boolean): void {
  const owner = host.closest("[data-block-id]");
  const blockId = owner instanceof HTMLElement ? (owner.dataset["blockId"] ?? "") : "";
  track("interact", {
    action,
    blockId,
    ...(value === undefined ? {} : { value }),
  });
}

/** A large, real button for sim controls. Never a bare SVG shape with a click handler. */
export function simButton(label: string, onClick: () => void, primary = false): HTMLButtonElement {
  const btn = el("button", {
    class: primary ? "lesson-btn lesson-btn--primary" : "lesson-btn",
    text: label,
    attrs: { type: "button" },
  });
  btn.addEventListener("click", onClick);
  return btn;
}
